import { action, computed, observable } from 'mobx'
import {
  asapScheduler,
  defer,
  distinctUntilChanged,
  type Observable,
  subscribeOn,
  switchMap,
} from 'rxjs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import { type DocumentTypeStore } from '@/features/documents/DocumentTypeStore'
import { type Member } from '@/features/iam/members/Member'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { Membership } from '@/features/iam/workspaces/Membership'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import type { Direction } from '@/infra/paging'
import type { FetchJobsFn, SearchJobsFn } from '@/features/jobs'
import { type Id } from '@/infra/id'
import { ViewModelList } from '@/infra/view-model-list'
import { tapAction } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { DocumentSearchViewModel } from '@/features/documents/DocumentSearchViewModel'
import type { InternalCreateDocumentManagementRowViewModelFn } from '@/features/documents/internal'
import {
  archiveManagedDocuments,
  type DocumentManagementActionContext,
  downloadManagedDocuments,
} from '@/features/documents/document-management-actions'
import { DeleteDocumentsDialogViewModel } from '@/features/documents/components/DocumentManagementTable/DeleteDocumentsDialog/DeleteDocumentsDialogViewModel'
import {
  type ColumnSortOrder,
  DocumentsTableColumns,
  type StaticColumnDetails,
} from '@/features/documents/document-table-columns'

/**
 * View model for managing a user's documents as an admin.
 */
export class DocumentManagementViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _rowVms

  private readonly _actionContext: DocumentManagementActionContext

  /**
   * The set of selected document long ids.
   */
  readonly selectedDocumentLongIds = observable.set<Id>()

  /**
   * The document management table columns.
   */
  readonly columns = DocumentsTableColumns

  readonly deleteDialogVm

  readonly searcher

  /**
   * Update workspaces filter when current workspace is changed, once the
   * filter has its options.
   */
  private readonly _workspaceSelectorUpdates$ = defer(() =>
    this.searcher.workspacesFilter.activate(),
  ).pipe(
    // The tracker subscribes before it activates the searcher, whose stored
    // filters replace the workspaces filter.
    subscribeOn(asapScheduler),
    switchMap(() => this._currentMember$),
    distinctUntilChanged(),
    tapAction((member) => {
      if (
        this.searcher.workspacesFilter.options.some(
          (o) => o.value === member.workspace.id,
        )
      ) {
        this.searcher.workspacesFilter.change([member.workspace.id])
      }
    }),
  )

  constructor(
    private readonly _userPublicId: () => Id,
    private readonly _userMemberships: () => Membership[],
    private readonly _currentUserMemberships: () => Membership[],
    private readonly _createRowViewModel: InternalCreateDocumentManagementRowViewModelFn,
    private readonly _documentStore: DocumentStore,
    private readonly _documentTypeStore: DocumentTypeStore,
    private readonly _fetchJobs: FetchJobsFn,
    private readonly _searchJobs: SearchJobsFn,
    private readonly _currentMember$: Observable<Member>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.deleteDialogVm = new DeleteDocumentsDialogViewModel(
      this._newAlert,
      this._documentStore,
    )

    this.searcher = new DocumentSearchViewModel(
      () => this.userPublicId,
      () => this.availableWorkspaces,
      this._documentTypeStore,
      this._searchJobs,
      this._documentStore,
      this._fetchJobs,
      this._createFilterStorage('UserDetailsDocumentManagement'),
      this._createAlertRetryWrapper,
    )

    this._actionContext = {
      documentStore: this._documentStore,
      newAlert: this._newAlert,
      createAlertErrorHandler: this._createAlertErrorHandler,
      selection: this.selectedDocumentLongIds,
      promptDelete: (documents, signal) =>
        this.deleteDialogVm.prompt(documents, signal),
    }

    this._rowVms = new ViewModelList({
      models: () => this.searcher.documents,
      create: (document) =>
        this._createRowViewModel(
          document,
          (doc) => this.searcher.linkedJobs(doc),
          this._actionContext,
        ),
    })

    this._lifecycle
      .manage(this.searcher)
      .manage(this._rowVms)
      .manage(this._workspaceSelectorUpdates$)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The currently selected user.
   */
  @computed
  get userPublicId() {
    return this._userPublicId()
  }

  /**
   * The currently selected user memberships.
   */
  @computed
  get userMemberships() {
    return this._userMemberships()
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * (in regard to managing documents) AND that the current user is a member of.
   */
  @computed
  get availableWorkspaces() {
    const isPlatformAdmin = this._getCurrentMember().user.platformAdmin
    const userWorkspaces = this.userMemberships.map((m) => m.workspace)

    if (isPlatformAdmin) {
      return userWorkspaces
    }

    const adminWorkspaces = this._currentUserMemberships()
      .filter((m) => m.member.permissions.adminDocumentManage)
      .map((m) => m.workspace)

    return userWorkspaces.filter((uw) => adminWorkspaces.includes(uw))
  }

  /**
   * View models for the searched documents.
   */
  @computed
  get rowVms() {
    return this._rowVms.items
  }

  /**
   * The table's column headers with their sort order.
   */
  @computed
  get columnHeaders() {
    return this.columns.map((col) => ({
      id: col.id,
      header: col.header,
      sortable: col.sortable,
      sortOrder: this._getSortOrder(col),
    }))
  }

  /**
   * Gets the sort order for the specified column.
   */
  private _getSortOrder(column: StaticColumnDetails): ColumnSortOrder {
    return this.searcher.sorting.field === column.field
      ? parseDirection(this.searcher.sorting.direction)
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  @action.bound
  setSortOrder(columnId: string, direction: ColumnSortOrder) {
    const column = this.columns.find((c) => c.id === columnId)
    if (!column?.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(toDirection(direction))
  }

  /**
   * Triggers the download of the selected documents (latest version only).
   */
  @action.bound
  downloadSelectedDocuments() {
    return downloadManagedDocuments(
      this._actionContext,
      this._selectedDocuments,
    )
  }

  /**
   * Triggers the archival of the selected documents.
   */
  @action.bound
  archiveSelectedDocuments() {
    return archiveManagedDocuments(
      this._actionContext,
      this._selectedDocuments,
      this._lifecycle.signal,
    )
  }

  /**
   * The loaded documents that are selected.
   */
  private get _selectedDocuments() {
    return this.searcher.scroller.items.filter((doc) =>
      this.selectedDocumentLongIds.has(doc.id),
    )
  }
}

function parseDirection(direction?: Direction): ColumnSortOrder {
  return direction ?? 'none'
}

function toDirection(sortOrder: ColumnSortOrder): Direction | undefined {
  return sortOrder === 'none' ? undefined : sortOrder
}
